import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Star } from 'lucide-react';
import style from './ClientRecipeDetail.module.css';
import ClientCommentList from "@/ui/pages/client/ClientCommentList/ClientCommentList";
import ClientReviewModal from '@/ui/pages/client/ClientReviewModal/ClientReviewModal';
import backBtnNew from '@/ui/assets/icons/backBtnNew.svg';
import favBtn from '@/ui/assets/icons/favBtn.svg';
import { doc, getDoc } from "firebase/firestore";
import { db } from "@config/firebaseConfig";
import ProductImageCarousel from '@/ui/components/client/ProductImageCarousel/ProductImageCarousel';
import heroStyle from '@/ui/components/client/ProductDetailHero/ProductDetailHero.module.css';
import { getOrCreateReviewerId } from '@/utils/reviewerId';
import { sortCommentsNewestFirst } from '@/utils/commentDates';
import {
    isProductLikedLocally,
    isLikedByUser,
    setProductLikedLocally,
} from '@/utils/productLikes';
import { toggleProductLike } from '@services/clientProductLikesFirebaseService';
import { ROUTES } from '@/config/routes';
import { useScrollToTop } from '@/utils/useScrollToTop';

const calculateAverageRating = (comments) => {
    if (!comments?.length) return 0;
    const totalStars = comments.reduce(
        (acc, c) => acc + Number(c.stars_given || 0),
        0
    );
    return totalStars / comments.length;
};

const formatOpinionesLabel = (count) => {
    if (count === 1) return '1 opinión';
    return `${count} opiniones`;
};

const ClientRecipeDetail = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { productId: productIdFromRoute } = useParams();
    const dataFromCatalog = location.state;

    const productIdFromStorage = (() => {
        try {
            return localStorage.getItem('client_selected_product_id');
        } catch {
            return null;
        }
    })();

    const productId =
        productIdFromRoute ||
        dataFromCatalog?.id ||
        dataFromCatalog?._id ||
        productIdFromStorage;

    const containerRef = useScrollToTop(productId);

    const [showModal, setShowModal] = useState(false);
    const [reviewerId] = useState(() => getOrCreateReviewerId());
    const [loading, setLoading] = useState(false);
    const [likesCount, setLikesCount] = useState(dataFromCatalog?.likes_count || 0);
    const [hasLiked, setHasLiked] = useState(() =>
        productId ? isProductLikedLocally(productId) : false
    );
    const [heartPulse, setHeartPulse] = useState(false);
    const [comments, setComments] = useState(dataFromCatalog?.comments || []);

    useEffect(() => {
        if (!productId) return;

        const loadProductData = async () => {
            try {
                const snap = await getDoc(doc(db, 'products', productId));
                if (!snap.exists()) return;

                const data = snap.data();
                if (Array.isArray(data.comments)) {
                    setComments(sortCommentsNewestFirst(data.comments));
                }
                if (typeof data.likes_count === 'number') {
                    setLikesCount(data.likes_count);
                }

                const liked = isLikedByUser(data.liked_by, reviewerId);
                setHasLiked(liked);
                setProductLikedLocally(productId, liked);
            } catch (error) {
                console.error('Error al cargar el producto:', error);
            }
        };

        loadProductData();
    }, [productId, reviewerId]);

    const galleryImages = useMemo(() => {
        const urls = dataFromCatalog?.image_urls;
        if (Array.isArray(urls) && urls.length > 0) {
            return urls.filter(Boolean);
        }
        if (dataFromCatalog?.image_url) {
            return [dataFromCatalog.image_url];
        }
        return [];
    }, [dataFromCatalog]);

    const averageRating = useMemo(
        () => calculateAverageRating(comments),
        [comments]
    );

    const opinionsCount = comments.length;

    const myComment = useMemo(
        () => comments.find((c) => c.user_id === reviewerId) || null,
        [comments, reviewerId]
    );

    const userStars = myComment ? Number(myComment.stars_given) || 0 : 0;

    const product = {
        id: productId,
        name: dataFromCatalog?.name || 'Producto sin nombre',
        price: dataFromCatalog?.price || 0,
        description: dataFromCatalog?.description || 'Sin descripción disponible.',
        image_url: galleryImages[0] || dataFromCatalog?.image_url || 'https://via.placeholder.com/400',
        image_urls: galleryImages,
    };

    const [isExpanded, setIsExpanded] = useState(false);
    const DESCRIPTION_LIMIT = 200;
    const showToggle = product.description && product.description.length > DESCRIPTION_LIMIT;
    const displayedDescription = showToggle && !isExpanded
        ? product.description.slice(0, DESCRIPTION_LIMIT) + '...'
        : product.description;

    const handleCommentSaved = (savedComment, isEdit) => {
        setComments((prev) => {
            const next = isEdit
                ? prev.map((c) =>
                    c.user_id === savedComment.user_id ? { ...c, ...savedComment } : c
                )
                : [...prev, savedComment];
            return sortCommentsNewestFirst(next);
        });
    };

    const sortedComments = useMemo(
        () => sortCommentsNewestFirst(comments),
        [comments]
    );

    const handleToggleLike = async () => {
        if (!product.id || loading) return;

        const wasLiked = hasLiked;
        const previousCount = likesCount;
        const willLike = !wasLiked;

        setHasLiked(willLike);
        setLikesCount((prev) => (willLike ? prev + 1 : Math.max(0, prev - 1)));
        setProductLikedLocally(product.id, willLike);
        setHeartPulse(true);

        setLoading(true);

        try {
            await toggleProductLike(product.id, reviewerId, wasLiked);
        } catch (error) {
            console.error("Error al procesar el me gusta:", error);
            setHasLiked(wasLiked);
            setLikesCount(previousCount);
            setProductLikedLocally(product.id, wasLiked);
            alert("No se pudo actualizar tu me gusta. Inténtalo de nuevo.");
        } finally {
            setLoading(false);
            setTimeout(() => setHeartPulse(false), 350);
        }
    };

    const renderStars = (rating, activeColor = '#FFB84D', inactiveColor = '#EEDBD1') => {
        const clampedRating = Math.min(Math.max(Number(rating) || 0, 0), 5);

        return Array.from({ length: 5 }, (_, i) => {
            const fillRatio = Math.min(Math.max(clampedRating - i, 0), 1);

            return (
                <span key={i} className={style.starWrapper} aria-hidden="true">
                    <Star
                        size={22}
                        fill={inactiveColor}
                        color={inactiveColor}
                        strokeWidth={1.5}
                        className={style.starIcon}
                    />
                    {fillRatio > 0 && (
                        <span
                            className={style.starFill}
                            style={{ width: `${fillRatio * 100}%` }}
                        >
                            <Star
                                size={22}
                                fill={activeColor}
                                color={activeColor}
                                strokeWidth={1.5}
                            />
                        </span>
                    )}
                </span>
            );
        });
    };

    const getMonthAbbr = () => {
        const meses = ['ene.', 'feb.', 'mar.', 'abr.', 'may.', 'jun.',
            'jul.', 'ago.', 'sep.', 'oct.', 'nov.', 'dic.'];
        return meses[new Date().getMonth()];
    };

    const handleBack = () => {
        if (location.key !== 'default') {
            navigate(-1);
        } else {
            navigate(ROUTES.CLIENT_HOME);
        }
    };

    return (
        <div ref={containerRef} className={style.detailContainer}>
            <div className={`${style.detailHeader} ${heroStyle.detailHeader}`}>
                <div className={heroStyle.imageWrapper}>
                    <ProductImageCarousel
                        images={product.image_urls}
                        alt={product.name}
                    />
                </div>

                <div className={heroStyle.heroGradient} aria-hidden="true" />

                <button
                    type="button"
                    className={style.backBtn}
                    onClick={handleBack}
                    aria-label="Volver"
                >
                    <img src={backBtnNew} alt="" size={22} />
                </button>

                <button
                    type="button"
                    className={style.favBtn}
                    onClick={handleToggleLike}
                    disabled={loading}
                    aria-label={hasLiked ? 'Quitar me gusta' : 'Dar me gusta'}
                    aria-pressed={hasLiked}
                >
                    <img
                        src={favBtn}
                        alt=""
                        className={`${style.favIcon} ${hasLiked ? style.liked : style.notLiked} ${heartPulse ? style.favActive : ''}`}
                    />
                </button>

            </div>

            <div className={`${style.detailInfo} ${heroStyle.detailInfoHero}`}>
                <h1>{product.name}</h1>

                <div className={style.starsRow}>
                    {renderStars(averageRating)}

                    <span className={style.reviewsCount}>
                        ({formatOpinionesLabel(opinionsCount)})
                    </span>

                    <span className={style.dot}>•</span>

                    <div className={style.likesInfo}>
                        <img
                            src={favBtn}
                            alt=""
                            className={`${style.likesIcon} ${hasLiked ? style.liked : style.notLiked}`}
                        />

                        <span className={style.likesCount}>
                            {likesCount}
                        </span>
                    </div>
                </div>

                <span className={style.priceTag}>
                    $ {Number(product.price).toLocaleString('es-AR')}
                </span>

                <p className={style.description}>
                    {displayedDescription}
                    {showToggle && (
                        <button
                            type="button"
                            className={style.toggleDescriptionBtn}
                            onClick={() => setIsExpanded(!isExpanded)}
                        >
                            {isExpanded ? ' Ocultar' : ' Ver más'}
                        </button>
                    )}
                </p>

                <hr />

                <h3 className={style.ratingTitle}>¿Qué te pareció?</h3>

                <div
                    className={style.ratingInput}
                    onClick={() => setShowModal(true)}
                >
                    <div className={style.starsWithDate}>
                        {renderStars(userStars, '#FFB84D', '#EEDBD1')}
                        <span className={style.monthAbbr}>
                            {getMonthAbbr()}
                        </span>
                    </div>

                    <span className={style.dateInput}>
                        {myComment ? 'Editar mi opinión →' : 'Toca para calificar →'}
                    </span>
                </div>

                <ClientCommentList
                    productId={product.id}
                    comments={sortedComments}
                />
            </div>

            {showModal && (
                <ClientReviewModal
                    productId={product.id}
                    reviewerId={reviewerId}
                    existingComment={myComment}
                    onClose={() => setShowModal(false)}
                    onCommentSaved={handleCommentSaved}
                />
            )}
        </div>
    );
};

export default ClientRecipeDetail;